import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { getSupabase } from '../backend/supabaseAdapter';

// Google sign-in, built fresh for the TRUE V2 native project (task book §21):
//   - iOS client id + reversed URL scheme live in ios/App/App/Info.plist (see
//     docs/GOOGLE-SIGNIN-IOS.md, including the archive-time plist verification step);
//   - the plugin is imported statically — a dynamic import() chunk stall on device is a
//     documented legacy failure mode we do not reproduce;
//   - plugin cancellation REJECTS: a user backing out is a neutral outcome, never an error;
//   - THE ACCOUNT CHOOSER IS ALWAYS OFFERED (Owner device test 2026-09-21: 「没有给你选择登录哪个邮箱，
//     是直接登进我刚退出的谷歌账户」). The plugin's iOS provider answers a login with the PREVIOUS Google
//     user whenever GIDSignIn still holds one (GoogleProvider.swift: hasPreviousSignIn →
//     restorePreviousSignIn, no UI). `forcePrompt` skips that shortcut, and signing out of the app also
//     signs the Google SDK out (signOutOfGoogle), so neither path can pick an account silently.
//   - ANDROID (user walkthrough 2026-10-16 on the emulator): the plugin's Android provider REJECTS a login that
//     passes `scopes` unless MainActivity implements its marker interface ("You CANNOT use scopes without
//     modifying the main activity") — and its defaults are already email + profile + openid, so on Android
//     no scopes are passed. Android signs in with the WEB client id (Credential Manager), not the iOS one.
const iosClientId = (import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID as string | undefined)?.trim() ?? '';
const webClientId = (import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined)?.trim() ?? '';

let initialized = false;

function platformClientId(): string {
  return Capacitor.getPlatform() === 'android' ? webClientId : iosClientId;
}

// What `SocialLogin.login` is given per platform. Exported for the unit test; the Android branch is the
// 2026-10-16 fix, iOS keeps the explicit scopes + forcePrompt that the Owner's 2026-09-21 device test settled.
export function googleLoginOptions(platform: string): { scopes?: string[]; forcePrompt: boolean } {
  return platform === 'android' ? { forcePrompt: true } : { scopes: ['email', 'profile'], forcePrompt: true };
}

export interface GoogleSignInOutcome {
  ok: boolean;
  cancelled: boolean;
  message: string;
  // Set when the failure has its own words in the sign-in screen (i18n), so the raw text is not shown.
  code?: 'googleUnavailable' | 'notNative';
}

export function googleSignInAvailable(): boolean {
  return Capacitor.isNativePlatform() && platformClientId().length > 0 && getSupabase() !== null;
}

async function ensureInitialized(): Promise<void> {
  if (initialized) return;
  await SocialLogin.initialize({ google: { iOSClientId: iosClientId, webClientId } });
  initialized = true;
}

// Ends the Google SDK's own remembered user when the inspector signs out of the app. Best effort and
// native only: a failure here never keeps anyone signed in (the app session is already gone) — it only
// decides whether GIDSignIn still remembers somebody, and `forcePrompt` covers that case anyway.
export async function signOutOfGoogle(): Promise<void> {
  if (!Capacitor.isNativePlatform() || platformClientId().length === 0) return;
  try {
    await ensureInitialized();
    await SocialLogin.logout({ provider: 'google' });
  } catch {
    // nothing to end
  }
}

export async function signInWithGoogle(): Promise<GoogleSignInOutcome> {
  const client = getSupabase();
  if (!client) return { ok: false, cancelled: false, message: '后端未配置，无法登录。' };
  if (!Capacitor.isNativePlatform()) {
    return { ok: false, cancelled: false, code: 'notNative', message: 'Google 登录在 iOS / Android App 内可用；网页端请用邮箱登录。' };
  }
  if (platformClientId().length === 0) return { ok: false, cancelled: false, message: '这个版本还没配置 Google 登录，请先用邮箱登录。' };
  try {
    await ensureInitialized();
    const login = (await SocialLogin.login({
      provider: 'google',
      options: googleLoginOptions(Capacitor.getPlatform())
    })) as unknown as { result?: { idToken?: string | null } };
    const idToken = login.result?.idToken ?? null;
    if (!idToken) return { ok: false, cancelled: false, message: 'Google 未返回 idToken，登录失败。' };
    const { error } = await client.auth.signInWithIdToken({ provider: 'google', token: idToken });
    if (error) return { ok: false, cancelled: false, message: `登录失败：${error.message}` };
    return { ok: true, cancelled: false, message: '登录成功。' };
  } catch (raw) {
    return classifyGoogleSignInError(raw instanceof Error ? raw.message : String(raw));
  }
}

// What the plugin's failures mean to the inspector. Android Credential Manager answers a build whose signing
// certificate / package is not registered in the Google Cloud project with "[16] Account reauth failed" or
// "[28444] Developer console is not set up correctly" (emulator debug build, 2026-10-16) — a paragraph of
// developer troubleshooting the inspector cannot act on. They get one sentence; the raw text stays in the console.
export function classifyGoogleSignInError(text: string): GoogleSignInOutcome {
  if (/cancel/i.test(text)) return { ok: false, cancelled: true, message: '已取消登录。' };
  if (/\[16\]|\[28444\]|Developer console|reauth failed/i.test(text)) {
    console.warn('[googleSignIn] Google rejected this build:', text);
    return { ok: false, cancelled: false, code: 'googleUnavailable', message: 'Google 登录在这个版本里还没登记完成，暂时用不了。请先用邮箱登录。' };
  }
  return { ok: false, cancelled: false, message: `登录失败：${text}` };
}
